import { useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';

// #1155 — Pflicht-Kategorie beim Abschluss (Status → done)
// #1167 — Ein-Klick: Klick auf Kategorie = sofortiger Abschluss (kein extra Confirm-Button).
// Öffnet sich NUR wenn beim Wechsel auf "done" noch keine Kategorie gesetzt ist.
export default function DoneCategoryRequiredModal({ isOpen, allCategories, onCancel, onConfirm }) {
  const { t } = useLanguage();

  const options = useMemo(() => {
    return (allCategories || [])
      .filter(c => c && c.is_active !== false)
      .map(c => ({ id: c.id, name: c.name }));
  }, [allCategories]);

  const [activeIdx, setActiveIdx] = useState(-1);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setActiveIdx(-1);
      setBusy(false);
      busyRef.current = false;
    }
  }, [isOpen]);

  const triggerConfirm = async (id, idx) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setActiveIdx(idx);
    setBusy(true);
    try {
      await onConfirm?.(id);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); if (!busyRef.current) onCancel?.(); return; }
      if (e.key === 'Enter') {
        e.preventDefault();
        const opt = options[activeIdx];
        if (opt) triggerConfirm(opt.id, activeIdx);
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx(i => (options.length === 0 ? -1 : (i + 1) % options.length));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx(i => (options.length === 0 ? -1 : (i - 1 + options.length) % options.length));
        return;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, options, activeIdx, onCancel]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { if (!busyRef.current) onCancel?.(); }} />
      <div
        className="relative bg-white dark:bg-surface-card border border-[var(--border-default,#E5E7EB)] dark:border-line rounded-card w-full max-w-md p-6"
        style={{ fontFamily: 'DM Sans, system-ui, sans-serif' }}
      >
        <h2 className="text-lg font-semibold text-content mb-1">
          {t('cases.doneCategoryRequired.title', 'Kategorie erforderlich')}
        </h2>
        <p className="text-sm text-content-secondary mb-5">
          {t('cases.doneCategoryRequired.hintOneClick', 'Wähle eine Kategorie — der Case wird sofort abgeschlossen.')}
        </p>

        {options.length === 0 ? (
          <div className="text-sm text-content-secondary px-3 py-4 rounded-card border border-[var(--border-default,#E5E7EB)] dark:border-line bg-surface-input mb-4">
            {t('cases.doneCategoryRequired.empty', 'Keine aktiven Kategorien verfügbar. Bitte zuerst in den Einstellungen anlegen.')}
          </div>
        ) : (
          <div className="flex flex-col gap-2 mb-4 max-h-[50vh] overflow-y-auto pr-1">
            {options.map((opt, idx) => {
              const active = idx === activeIdx;
              const isBusyHere = busy && active;
              return (
                <button
                  key={opt.id}
                  type="button"
                  disabled={busy}
                  onClick={() => triggerConfirm(opt.id, idx)}
                  onMouseEnter={() => { if (!busy) setActiveIdx(idx); }}
                  className={
                    'w-full text-left flex items-center justify-between px-4 py-3 rounded-card border transition-colors ' +
                    (busy ? 'cursor-wait ' : 'cursor-pointer ') +
                    (active
                      ? 'border-[#338edc] bg-[#338edc]/10 text-content'
                      : 'border-[var(--border-default,#E5E7EB)] dark:border-line bg-surface-input text-content hover:border-[#338edc]/60')
                  }
                  style={active ? { boxShadow: 'inset 0 0 0 1px #338edc' } : undefined}
                >
                  <span className="text-sm font-medium">{opt.name}</span>
                  {isBusyHere ? (
                    <svg className="w-4 h-4 text-[#338edc] animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v4M12 16v4M4 12h4M16 12h4M6.34 6.34l2.83 2.83M14.83 14.83l2.83 2.83M6.34 17.66l2.83-2.83M14.83 9.17l2.83-2.83" />
                    </svg>
                  ) : active ? (
                    <svg className="w-4 h-4 text-[#338edc]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-end">
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="px-4 py-2 rounded-card border border-[var(--border-default,#E5E7EB)] dark:border-line bg-white dark:bg-surface-input text-sm font-medium text-content hover:bg-surface-hover transition-colors disabled:opacity-50"
          >
            {t('cases.doneCategoryRequired.cancel', 'Abbrechen')}
          </button>
        </div>
      </div>
    </div>
  );
}
